import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { colorsSemantic } from "../../theme/tokens/colors-semantic.js";
import TaskFilterTabs from "./TaskFilterTabs.jsx";
import TaskRow from "./TaskRow.jsx";

const VISIBLE_ROWS = 10;

/**
 * "Meine Aufgaben" — the dashboard's main work queue, already sorted by urgency
 * server-side (new first, then overdue, longest-waiting first within each band).
 *
 * Shows the top 10 of whatever the active filters leave. The pill counts come
 * from the server aggregates rather than the filtered array, so they keep
 * describing the whole workload instead of shrinking as you filter.
 */
export default function TaskListCard({
  tasks,
  filtered,
  counts = {},
  ageBuckets = [],
  meta = null,
  filters,
  onOpenTask,
  onShowAll,
}) {
  const { attention } = colorsSemantic;

  const visible = filtered.slice(0, VISIBLE_ROWS);
  const openTotal = meta?.open_total ?? tasks.length;
  const overdueCount = counts?.ueberfaellig ?? 0;

  const subtitle = filters.isFiltered
    ? `${filtered.length} von ${openTotal} ${openTotal === 1 ? "Vorgang" : "Vorgängen"}`
    : `${openTotal} ${openTotal === 1 ? "offener Vorgang" : "offene Vorgänge"} · ${overdueCount} überfällig`;

  const activeAgeLabel = filters.age
    ? ageBuckets.find((bucket) => bucket.key === filters.age)?.label
    : null;

  // Rows the client never received; the board can only filter what it has.
  const hiddenByLimit = filtered.length - visible.length;
  const truncatedOnServer = meta?.truncated ?? false;

  return (
    <Box
      sx={{
        backgroundColor: colorsSemantic.background.paper,
        borderRadius: "12px",
        border: `1px solid ${colorsSemantic.border.card}`,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "15px 18px 0",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: "13.5px", fontWeight: 700, color: colorsSemantic.text.strong, letterSpacing: "-.1px" }}
          >
            Meine Aufgaben
          </Typography>
          <Typography sx={{ fontSize: "11.5px", color: colorsSemantic.text.muted, mt: "3px" }}>
            {subtitle}
          </Typography>
        </Box>
        <Typography
          onClick={onShowAll}
          sx={{
            fontSize: "12px",
            color: colorsSemantic.text.accent.blue,
            fontWeight: 600,
            cursor: "pointer",
            flex: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Alle Schäden
        </Typography>
      </Box>

      <TaskFilterTabs counts={counts} active={filters.tab} onSelect={filters.setTab} />

      {activeAgeLabel && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "9px",
            padding: "10px 18px",
            backgroundColor: "#f6f9fd",
            borderBottom: `1px solid ${colorsSemantic.border.hairline}`,
          }}
        >
          <Typography sx={{ fontSize: "12px", color: colorsSemantic.text.accent.blue, fontWeight: 600 }}>
            {`Gefiltert: ${activeAgeLabel} seit letzter Aktion`}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={filters.clearAge}
            sx={{
              padding: "3px 9px",
              borderRadius: "20px",
              border: `1px solid ${attention.warn.border}`,
              backgroundColor: colorsSemantic.background.paper,
              color: colorsSemantic.text.accent.blue,
              fontSize: "11px",
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            Filter entfernen
          </Box>
        </Box>
      )}

      {visible.map((task) => (
        <TaskRow key={task.id} task={task} onOpen={onOpenTask} />
      ))}

      {visible.length === 0 && (
        <Typography
          sx={{ padding: "38px 18px", textAlign: "center", fontSize: "12.5px", color: colorsSemantic.text.muted }}
        >
          Keine Vorgänge in dieser Auswahl.
        </Typography>
      )}

      {(hiddenByLimit > 0 || truncatedOnServer) && (
        <Typography
          onClick={onShowAll}
          sx={{
            padding: "11px 18px",
            fontSize: "11.5px",
            color: colorsSemantic.text.muted,
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {truncatedOnServer
            ? `Weitere Vorgänge vorhanden — alle ${openTotal} unter „Alle Schäden“ anzeigen`
            : `${hiddenByLimit} weitere in dieser Auswahl — alle anzeigen`}
        </Typography>
      )}
    </Box>
  );
}

TaskListCard.propTypes = {
  tasks: PropTypes.array.isRequired,
  filtered: PropTypes.array.isRequired,
  counts: PropTypes.objectOf(PropTypes.number),
  ageBuckets: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, count: PropTypes.number })
  ),
  meta: PropTypes.shape({
    open_total: PropTypes.number,
    truncated: PropTypes.bool,
  }),
  filters: PropTypes.shape({
    tab: PropTypes.string,
    age: PropTypes.string,
    isFiltered: PropTypes.bool,
    setTab: PropTypes.func,
    clearAge: PropTypes.func,
  }).isRequired,
  onOpenTask: PropTypes.func.isRequired,
  onShowAll: PropTypes.func.isRequired,
};
